import React, { useEffect, useState, useRef } from "react";
import { api } from "@/api/client";
import { Bell, CheckCircle, Circle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const KIND_LABEL = {
  task_assigned: "Task",
  finance_pending: "Finance",
  invoice_created: "Invoice",
  wo_shared: "Work Order",
  mention: "Mention",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 45000); // gentle polling
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} }
    // Navigate to context if we have one
    if (n.kind === "finance_pending") nav("/finance");
    load(); setOpen(false);
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    load();
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell-wrap">
      <button
        data-testid="notification-bell"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative p-2 rounded-full bg-white border border-neutral-200 shadow-sm hover:bg-neutral-50 text-neutral-600 hover:text-neutral-900 transition-colors"
        title="Notifications"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-neutral-200 rounded-lg shadow-xl z-50 flex flex-col" style={{ maxHeight: "calc(100vh - 6rem)" }}>
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div className="font-display font-semibold text-sm text-neutral-900">Notifications</div>
            {unread > 0 && (
              <button data-testid="mark-all-read" onClick={markAllRead} className="text-xs text-neutral-500 hover:text-neutral-900">Mark all read</button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && (
              <div className="p-8 text-center text-xs text-neutral-500">You're all caught up.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => markRead(n)}
                className={`w-full text-left px-4 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors flex items-start gap-2 ${n.read ? "opacity-70" : ""}`}
              >
                <div className="pt-0.5">{n.read ? <CheckCircle size={14} className="text-neutral-400" /> : <Circle size={12} weight="fill" className="text-blue-500" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">{KIND_LABEL[n.kind] || n.kind}</div>
                  <div className="text-sm text-neutral-800 leading-tight">{n.message}</div>
                  <div className="text-[10px] text-neutral-400 mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
